import { type ProviderDriverKind, type ProviderInstanceId } from "@t3tools/contracts";
import { memo, type ReactNode } from "react";
import { LockIcon, StarIcon } from "lucide-react";
import {
  COST_BADGE_META,
  getDisplayModelName,
  getTriggerDisplayModelLabel,
  type Icon,
  type ModelCostTier,
  type ModelEsque,
  type ModelTier,
  PROVIDER_ICON_BY_PROVIDER,
  TIER_BADGE_META,
  UTILITY_BADGE_META,
} from "./providerIconUtils";
import type { ModelCapability } from "./morrowModelCatalog";
import { ComboboxItem } from "../ui/combobox";
import { Button } from "../ui/button";
import { Kbd } from "../ui/kbd";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { cn } from "~/lib/utils";
import { modelPickerModelKey } from "./modelPickerKeys";

const CAPABILITY_LABEL: Record<ModelCapability, string> = {
  vision: "Vision",
  tools: "Tools",
  reasoning: "Reasoning",
};

function CapabilityBadge({ children }: { children: ReactNode }) {
  return (
    <span className="rounded-md border border-border px-1.5 text-[10px] font-semibold uppercase leading-[1.35] tracking-wide text-muted-foreground/80">
      {children}
    </span>
  );
}

function CostBadge({ cost }: { cost: ModelCostTier }) {
  const meta = COST_BADGE_META[cost];
  return (
    <span
      title={meta.title}
      className={cn(
        "rounded-md border px-1.5 text-[10px] font-semibold uppercase leading-[1.35] tracking-wide",
        meta.className,
      )}
    >
      {meta.label}
    </span>
  );
}

/** Colored tier badge: free (green), pay (amber), super (red). */
function TierBadge({ tier }: { tier: ModelTier }) {
  const meta = TIER_BADGE_META[tier];
  return (
    <span
      title={meta.title}
      className={cn(
        "rounded-md border px-1.5 text-[10px] font-semibold uppercase leading-[1.35] tracking-wide",
        meta.className,
      )}
    >
      {meta.label}
    </span>
  );
}

/** Dim badge for utility/background models (no user-facing tier). */
function BackgroundBadge() {
  return (
    <span
      title={UTILITY_BADGE_META.title}
      className={cn(
        "rounded-md border px-1.5 text-[10px] font-semibold uppercase leading-[1.35] tracking-wide",
        UTILITY_BADGE_META.className,
      )}
    >
      {UTILITY_BADGE_META.label}
    </span>
  );
}

/**
 * Renders the single trailing badge for a row: a background badge for utility
 * models, else the Morrow tier badge (free/pay/super), else the legacy cost
 * badge for real configured models.
 */
function RowTrailingBadge({ presentation }: { presentation?: ModelRowPresentation | undefined }) {
  if (!presentation) {
    return null;
  }
  if (presentation.isBackground) {
    return <BackgroundBadge />;
  }
  if (presentation.tier) {
    return <TierBadge tier={presentation.tier} />;
  }
  if (presentation.cost) {
    return <CostBadge cost={presentation.cost} />;
  }
  return null;
}

/** Extra t3.chat-style presentation for a row (description, badges, tier). */
export type ModelRowPresentation = {
  description?: string;
  capabilities?: ReadonlyArray<ModelCapability>;
  context?: string;
  /** Legacy cost tier ($/$$/$$$) — still supported for real model rows. */
  cost?: ModelCostTier;
  /** Morrow tier badge: free / pay / super. */
  tier?: ModelTier;
  /** Utility (background) model — renders a dim "bg" badge, no tier. */
  isBackground?: boolean;
};

function RowBody(props: {
  ProviderIcon: Icon | null;
  providerLabel: string;
  title: ReactNode;
  showProvider: boolean;
  showNewBadge?: boolean | undefined;
  presentation?: ModelRowPresentation | undefined;
}) {
  const { presentation } = props;
  const capabilities = presentation?.capabilities ?? [];
  return (
    <div className="min-w-0 flex-1 text-left">
      <div className="flex min-w-0 items-center gap-2">
        <div className="min-w-0 truncate text-xs font-medium leading-snug">{props.title}</div>
        {props.showNewBadge ? (
          <span
            className="shrink-0 rounded border border-update/35 bg-update/15 px-0.5 py-px text-[10px] font-bold uppercase leading-none tracking-wide text-update-foreground"
            aria-label="New model"
          >
            New
          </span>
        ) : null}
      </div>
      {presentation?.description ? (
        <div className="mt-0.5 truncate text-xs leading-snug text-muted-foreground/70">
          {presentation.description}
        </div>
      ) : null}
      {capabilities.length > 0 || presentation?.context ? (
        <div className="mt-1 flex flex-wrap items-center gap-1">
          {capabilities.map((cap) => (
            <CapabilityBadge key={cap}>{CAPABILITY_LABEL[cap]}</CapabilityBadge>
          ))}
          {presentation?.context ? <CapabilityBadge>{presentation.context}</CapabilityBadge> : null}
        </div>
      ) : null}
      {props.showProvider && (
        <div className="mt-1 flex items-center gap-1.5">
          {props.ProviderIcon ? <props.ProviderIcon className="size-3 shrink-0" /> : null}
          <span className="truncate text-xs font-normal leading-snug text-muted-foreground/70">
            {props.providerLabel}
          </span>
        </div>
      )}
    </div>
  );
}

export const ModelListRow = memo(function ModelListRow(props: {
  index: number;
  model: ModelEsque;
  /** Instance the model belongs to — the routing key used in combobox values. */
  instanceId: ProviderInstanceId;
  /** Driver kind of the instance — used for the provider icon glyph. */
  driverKind: ProviderDriverKind;
  /**
   * Display name to show in the secondary line (provider footer). Usually
   * the instance's configured `displayName` so custom instances like
   * "Codex Personal" render with their user-authored label.
   */
  providerDisplayName: string;
  providerAccentColor?: string | undefined;
  isFavorite: boolean;
  isSelected: boolean;
  showProvider: boolean;
  preferShortName?: boolean;
  useTriggerLabel?: boolean;
  showNewBadge?: boolean;
  jumpLabel?: string | null;
  disabledReason?: string | null;
  /** Optional richer presentation (description + capability/cost badges). */
  presentation?: ModelRowPresentation;
  /**
   * When true, this is a locked/preview showcase row: it renders dimmed with a
   * lock, is NOT selectable (not a combobox item), and clicking it triggers
   * {@link onUpgrade} instead of a model change.
   */
  isLocked?: boolean;
  /** Icon override — used by preview rows whose provider isn't in the real map. */
  providerIcon?: Icon | null;
  onUpgrade?: () => void;
  onToggleFavorite: () => void;
}) {
  const ProviderIcon = props.providerIcon ?? PROVIDER_ICON_BY_PROVIDER[props.driverKind] ?? null;
  const providerLabel = props.model.subProvider
    ? `${props.providerDisplayName} · ${props.model.subProvider}`
    : props.providerDisplayName;

  const title = props.useTriggerLabel
    ? getTriggerDisplayModelLabel(props.model)
    : getDisplayModelName(
        props.model,
        props.preferShortName ? { preferShortName: true } : undefined,
      );

  const favoriteStar = (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            size="icon-xs"
            variant="ghost"
            className={cn(
              "-mr-1 shrink-0 text-muted-foreground/70 opacity-64 transition-[color,opacity] hover:text-foreground hover:opacity-100 group-hover:opacity-100",
              props.isFavorite && "text-foreground opacity-100",
            )}
            onClick={(event) => {
              event.stopPropagation();
              props.onToggleFavorite();
            }}
            onKeyDown={(event) => {
              event.stopPropagation();
            }}
            disabled={props.isLocked || Boolean(props.disabledReason)}
            aria-label={props.isFavorite ? "Remove from favorites" : "Add to favorites"}
          >
            <StarIcon
              className={cn(
                "size-3.5 sm:size-3",
                props.isFavorite && "fill-current text-yellow-500",
              )}
            />
          </Button>
        }
      />
      <TooltipPopup side="top" align="center">
        {props.isFavorite ? "Remove from favorites" : "Add to favorites"}
      </TooltipPopup>
    </Tooltip>
  );

  // Locked preview rows render as a DISABLED ComboboxItem: Base UI skips
  // disabled items in keyboard navigation and never fires onValueChange for
  // them, so they can never dispatch a model change. They keep their list index
  // slot (so real-row indices stay aligned) but re-enable pointer events so a
  // click can trigger the upgrade / add-key flow instead.
  if (props.isLocked) {
    return (
      <ComboboxItem
        hideIndicator
        index={props.index}
        value={modelPickerModelKey(props.instanceId, props.model.slug)}
        disabled
        contentClassName="flex w-full items-start gap-3"
        className="group relative w-full !min-w-0 max-w-full cursor-pointer rounded-md px-2 py-2 opacity-60 transition-[background-color,opacity] data-disabled:pointer-events-auto hover:bg-[color-mix(in_srgb,var(--popover)_90%,var(--foreground))] hover:opacity-80"
        onClick={(event) => {
          event.stopPropagation();
          props.onUpgrade?.();
        }}
      >
        <RowBody
          ProviderIcon={ProviderIcon}
          providerLabel={providerLabel}
          title={title}
          showProvider={props.showProvider}
          showNewBadge={props.showNewBadge}
          presentation={props.presentation}
        />
        <div className="flex shrink-0 items-center gap-1.5">
          <RowTrailingBadge presentation={props.presentation} />
          <Tooltip>
            <TooltipTrigger
              render={
                <span className="inline-flex size-5 shrink-0 items-center justify-center text-muted-foreground/70">
                  <LockIcon className="size-3.5 sm:size-3" />
                </span>
              }
            />
            <TooltipPopup side="top" align="center">
              Preview only — not selectable yet
            </TooltipPopup>
          </Tooltip>
        </div>
      </ComboboxItem>
    );
  }

  const row = (
    <ComboboxItem
      hideIndicator
      index={props.index}
      value={modelPickerModelKey(props.instanceId, props.model.slug)}
      disabled={Boolean(props.disabledReason)}
      contentClassName="flex w-full items-start gap-3"
      className={cn(
        "group relative w-full !min-w-0 max-w-full cursor-pointer rounded-md px-2 py-2 transition-[background-color,box-shadow,color]",
        "hover:bg-[color-mix(in_srgb,var(--popover)_90%,var(--foreground))] data-highlighted:bg-[color-mix(in_srgb,var(--popover)_90%,var(--foreground))] data-selected:bg-foreground/[0.08] data-selected:text-foreground data-selected:ring-0 [&[data-highlighted][data-selected]]:bg-[color-mix(in_srgb,var(--popover)_90%,var(--foreground))]",
        props.disabledReason &&
          "data-disabled:pointer-events-auto data-disabled:cursor-not-allowed data-disabled:hover:bg-transparent",
      )}
    >
      <RowBody
        ProviderIcon={ProviderIcon}
        providerLabel={providerLabel}
        title={title}
        showProvider={props.showProvider}
        showNewBadge={props.showNewBadge}
        presentation={props.presentation}
      />

      <div className="flex shrink-0 items-center gap-1.5">
        <RowTrailingBadge presentation={props.presentation} />
        {props.jumpLabel ? (
          <Kbd className="h-4 min-w-0 rounded-sm px-1.5 text-[10px]">{props.jumpLabel}</Kbd>
        ) : null}
        {favoriteStar}
      </div>
    </ComboboxItem>
  );

  if (!props.disabledReason) {
    return row;
  }

  return (
    <Tooltip>
      <TooltipTrigger render={row} />
      <TooltipPopup side="left" align="center" className="max-w-64 text-balance leading-snug">
        {props.disabledReason}
      </TooltipPopup>
    </Tooltip>
  );
});
